import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../Layout/Layout';
import {
    FiLock,
    FiClock,
    FiAlertCircle,
    FiX,
    FiArrowRight,
    FiAward,
    FiRefreshCw,
    FiShield,
} from 'react-icons/fi';
import api from '../utils/apiClient';
import useAntiCheat from '../hooks/useAntiCheat';
import SecurityAlert from '../components/SecurityAlert';
import CodeBlock from '../components/CodeBlock';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';

const QuizContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const QuizHeader = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  border: 1px solid ${({ theme }) => theme.colors.divider};
`;

const QuizTitle = styled.h1`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const QuizDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const QuizMeta = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
  }
`;

const QuizInstructions = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
  
  h3 {
    display: flex;
    align-items: center;
    font-size: ${({ theme }) => theme.fonts.sizes.md};
    margin-bottom: ${({ theme }) => theme.spacing[2]};
    
    svg {
      margin-right: ${({ theme }) => theme.spacing[2]};
    }
  }
  
  p {
    margin-bottom: ${({ theme }) => theme.spacing[2]};
    font-size: ${({ theme }) => theme.fonts.sizes.sm};
    color: ${({ theme }) => theme.colors.textSecondary};
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const StartButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[6]};
  background: ${({ theme }) => theme.colors.gradientPrimary};
  color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  transition: all ${({ theme }) => theme.transitions.fast};
  width: 100%;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &:not(:disabled):hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.glow};
  }
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
  }
`;

const QuizContent = styled.div`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  position: relative;
`;

const QuizProgress = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const ProgressText = styled.div`
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  
  span {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
  }
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 8px;
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  margin: 0 ${({ theme }) => theme.spacing[4]};
  overflow: hidden;
`;

const ProgressFill = styled.div`
  width: ${({ progress }) => `${progress}%`};
  height: 100%;
  background: ${({ theme }) => theme.colors.gradientPrimary};
  transition: width 0.3s ease;
`;

const QuestionContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const QuestionText = styled.div`
  font-size: ${({ theme }) => theme.fonts.sizes.lg};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  line-height: 1.5;
`;

const QuestionTypeIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  color: ${({ theme }) => theme.colors.textSecondary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[1]};
  }
`;

const QuestionImage = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  text-align: center;
  
  img {
    max-width: 100%;
    max-height: 300px;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    border: 1px solid ${({ theme }) => theme.colors.divider};
  }
`;

const CodeContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.divider};
`;

const AnswersList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const AnswerOption = styled.div`
  position: relative;
  background-color: ${({ theme, selected, correct, incorrect, showAnswers }) => {
        if (!showAnswers) {
            return selected ? theme.colors.cardGlow : theme.colors.backgroundLighter;
        } else {
            if (correct) return `${theme.colors.success}20`;
            if (incorrect) return `${theme.colors.error}20`;
            return theme.colors.backgroundLighter;
        }
    }};
  border: 1px solid ${({ theme, selected, correct, incorrect, showAnswers }) => {
        if (!showAnswers) {
            return selected ? theme.colors.primary : theme.colors.divider;
        } else {
            if (correct) return theme.colors.success;
            if (incorrect) return theme.colors.error;
            return theme.colors.divider;
        }
    }};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[4]};
  cursor: ${({ showAnswers, disabled }) => (showAnswers || disabled) ? 'default' : 'pointer'};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    ${({ showAnswers, disabled, theme }) => (!showAnswers && !disabled) && `
      transform: translateY(-2px);
      border-color: ${theme.colors.primary};
      background-color: ${theme.colors.cardGlow};
    `}
  }
`;

const AnswerLabel = styled.div`
  display: flex;
  align-items: center;
`;

const OptionLabel = styled.div`
  width: 28px;
  height: 28px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background-color: ${({ theme, selected, correct, incorrect, showAnswers }) => {
        if (!showAnswers) {
            return selected ? theme.colors.primary : theme.colors.backgroundLight;
        } else {
            if (correct) return theme.colors.success;
            if (incorrect) return theme.colors.error;
            return theme.colors.backgroundLight;
        }
    }};
  color: ${({ theme, selected, correct, incorrect, showAnswers }) => {
        if (!showAnswers) {
            return selected ? theme.colors.background : theme.colors.textPrimary;
        } else {
            if (correct || incorrect) return theme.colors.background;
            return theme.colors.textPrimary;
        }
    }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-right: ${({ theme }) => theme.spacing[3]};
  flex-shrink: 0;
`;

const AnswerText = styled.div`
  flex: 1;
`;

const AnswerImage = styled.div`
  margin-top: ${({ theme }) => theme.spacing[2]};
  text-align: center;
  
  img {
    max-width: 100%;
    max-height: 150px;
    border-radius: ${({ theme }) => theme.borderRadius.md};
  }
`;

const FillInInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 2px solid ${({ theme, isValid, showValidation }) => {
        if (!showValidation) return theme.colors.divider;
        return isValid ? theme.colors.success : theme.colors.error;
    }};
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  font-size: ${({ theme }) => theme.fonts.sizes.md};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => `${theme.colors.primary}30`};
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.spacing[6]};
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background-color: ${({ theme, primary }) =>
        primary ? theme.colors.primary : theme.colors.backgroundLighter};
  color: ${({ theme, primary }) =>
        primary ? theme.colors.background : theme.colors.textPrimary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ primary }) => primary ? '600' : '400'};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    background-color: ${({ theme, primary }) =>
        primary ? theme.colors.primaryDark : theme.colors.backgroundLight};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    margin-left: ${({ theme, left }) => left ? '0' : theme.spacing[2]};
    margin-right: ${({ theme, left }) => left ? theme.spacing[2] : '0'};
  }
`;

const ResultsContainer = styled.div`
  text-align: center;
`;

const ResultIcon = styled.div`
  width: 72px;
  height: 72px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background-color: ${({ theme, passed }) =>
        passed ? theme.colors.success : theme.colors.error};
  color: ${({ theme }) => theme.colors.background};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${({ theme }) => theme.spacing[4]};
  
  svg {
    font-size: 36px;
  }
`;

const ResultTitle = styled.h2`
  font-size: ${({ theme }) => theme.fonts.sizes['2xl']};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  color: ${({ theme, passed }) =>
        passed ? theme.colors.success : theme.colors.error};
`;

const ResultText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const ScoreContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const ScoreCircle = styled.div`
  width: 120px;
  height: 120px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background-color: ${({ theme, passed }) =>
        passed ? `${theme.colors.success}20` : `${theme.colors.error}20`};
  border: 4px solid ${({ theme, passed }) =>
        passed ? theme.colors.success : theme.colors.error};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${({ theme, passed }) =>
        passed ? theme.colors.success : theme.colors.error};
  font-size: ${({ theme }) => theme.fonts.sizes['3xl']};
  font-weight: 700;
  
  span {
    font-size: ${({ theme }) => theme.fonts.sizes.sm};
    color: ${({ theme }) => theme.colors.textSecondary};
    margin-top: ${({ theme }) => theme.spacing[1]};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background-color: ${({ theme, primary }) =>
        primary ? theme.colors.primary : 'transparent'};
  color: ${({ theme, primary }) =>
        primary ? theme.colors.background : theme.colors.primary};
  border: ${({ theme, primary }) =>
        primary ? 'none' : `1px solid ${theme.colors.primary}`};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    background-color: ${({ theme, primary }) =>
        primary ? theme.colors.primaryDark : `${theme.colors.primary}10`};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[2]};
  }
`;

const SecureEnvironmentBadge = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme, active }) =>
        active ? `${theme.colors.success}20` : `${theme.colors.error}20`};
  color: ${({ theme, active }) =>
        active ? theme.colors.success : theme.colors.error};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  position: absolute;
  top: ${({ theme }) => theme.spacing[3]};
  left: 50%;
  transform: translateX(-50%);
  font-family: 'Courier New', monospace;
  border: 1px solid ${({ theme, active }) =>
        active ? `${theme.colors.success}40` : `${theme.colors.error}40`};
  box-shadow: 0 0 10px ${({ theme, active }) =>
        active ? `${theme.colors.success}20` : `${theme.colors.error}20`};
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[1]};
  }
`;

const CooldownContainer = styled.div`
  background-color: ${({ theme }) => `${theme.colors.error}10`};
  border: 1px solid ${({ theme }) => `${theme.colors.error}30`};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const CooldownIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: ${({ theme }) => `${theme.colors.error}20`};
  color: ${({ theme }) => theme.colors.error};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${({ theme }) => theme.spacing[4]};
  font-size: 32px;
`;

const CooldownTitle = styled.h3`
  font-size: ${({ theme }) => theme.fonts.sizes.xl};
  color: ${({ theme }) => theme.colors.error};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const CooldownText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const CooldownTimer = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const TimerUnit = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TimerValue = styled.div`
  font-size: ${({ theme }) => theme.fonts.sizes["3xl"]};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  min-width: 60px;
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
`;

const TimerLabel = styled.div`
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

const Timer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  color: ${({ theme, warning }) => warning ? theme.colors.error : theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  
  svg {
    margin-right: ${({ theme }) => theme.spacing[1]};
  }
`;

const FinalQuizPage = () => {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const imageRefs = useRef({});
    const cooldownIntervalRef = useRef(null);
    const autoSubmitTriggered = useRef(false); // Prevent multiple auto-submissions

    const [quizData, setQuizData] = useState(null);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [textAnswers, setTextAnswers] = useState({});
    const [quizStarted, setQuizStarted] = useState(false);
    const [quizCompleted, setQuizCompleted] = useState(false);
    const [quizResults, setQuizResults] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [securityAlert, setSecurityAlert] = useState({
        show: false,
        severity: 'warning',
        title: '',
        message: '',
        code: ''
    });
    const [secureEnvironment, setSecureEnvironment] = useState(true);
    const [cooldownRemaining, setCooldownRemaining] = useState(null);
    const [canRetake, setCanRetake] = useState(true);
    const [availabilityMessage, setAvailabilityMessage] = useState('');
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [timePerQuestion, setTimePerQuestion] = useState(60); // 60 seconds per question by default

    // Handle the auto-submission when max violations are reached
    const handleMaxViolations = () => {
        console.log('handleMaxViolations called - quizCompleted:', quizCompleted, 'autoSubmitTriggered:', autoSubmitTriggered.current);

        if (!quizCompleted && !autoSubmitTriggered.current) {
            autoSubmitTriggered.current = true;
            console.log('Auto-submit triggered due to max violations - setting 3 second timer');

            setSecurityAlert({
                show: true,
                severity: 'critical',
                title: 'SECURITY PROTOCOL BREACH',
                message: 'Multiple security violations detected. Assessment session terminated. Your answers have been collected and the incident has been logged.',
                code: 'FORCED_TERMINATION_PROTOCOL_ENGAGED'
            });

            setTimeout(() => {
                console.log('setTimeout executed - calling submitQuiz now');
                submitQuiz();
            }, 3000);
        } else {
            console.log('Auto-submit blocked - conditions not met (quizCompleted or already triggered)');
        }
    };

    // Handle violations
    const handleViolation = (type, details) => {
        setSecureEnvironment(false);

        // Show the security alert
        setSecurityAlert({
            show: true,
            severity: type === 'tab_switch' ? 'critical' : 'warning',
            title: type === 'tab_switch' ? 'PERIMETER BREACH' : 'DATA EXFILTRATION ATTEMPT',
            message: details.message,
            code: details.code
        });

        // Reset secure environment status after alert is closed
        setTimeout(() => {
            setSecureEnvironment(true);
        }, 5000);
    };


    const { violations, resetViolations } = useAntiCheat({
        maxViolations: 3,  // Configure to submit after 3 violations
        onMaxViolationsReached: quizStarted && !quizCompleted ? handleMaxViolations : () => { },
        onViolation: quizStarted && !quizCompleted ? handleViolation : () => { },
        detectRightClick: quizStarted && !quizCompleted,
        detectCopy: quizStarted && !quizCompleted,
        detectPaste: quizStarted && !quizCompleted,
        detectDevTools: quizStarted && !quizCompleted,
        detectTabSwitch: quizStarted && !quizCompleted,
        detectScreenCapture: quizStarted && !quizCompleted
    });

    // Keep a ref of violations to ensure we always submit the latest count
    const violationsRef = useRef(0);
    useEffect(() => {
        violationsRef.current = violations;
    }, [violations]);

    // Clear security alerts when quiz completes
    useEffect(() => {
        if (quizCompleted) {
            setSecurityAlert({
                show: false,
                severity: 'warning',
                title: '',
                message: '',
                code: ''
            });
        }
    }, [quizCompleted]);

    // Initialize quiz timer
    useEffect(() => {
        if (quizData && quizStarted && !quizCompleted) {
            // Set total time based on number of questions
            const totalTime = quizData.quiz.estimated_time;
            setTimeRemaining(totalTime * 60);

            const timer = setInterval(() => {
                setTimeRemaining(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        submitQuiz();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(timer);
        }
    }, [quizStarted, quizData, quizCompleted]);

    // Format time remaining
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' + secs : secs}`;
    };

    // Check cooldown
    const clearCooldownTimer = () => {
        if (cooldownIntervalRef.current) {
            clearInterval(cooldownIntervalRef.current);
            cooldownIntervalRef.current = null;
        }
    };

    const startCooldownTimer = (totalSeconds) => {
        if (typeof totalSeconds !== 'number') {
            return;
        }

        const sanitizedSeconds = Math.max(0, Math.floor(totalSeconds));

        if (sanitizedSeconds === 0) {
            clearCooldownTimer();
            setCooldownRemaining(0);
            setCanRetake(true);
            return;
        }

        setCooldownRemaining(sanitizedSeconds);
        setCanRetake(false);

        clearCooldownTimer();

        cooldownIntervalRef.current = setInterval(() => {
            setCooldownRemaining(prev => {
                if (prev === null) {
                    return null;
                }

                if (prev <= 1) {
                    clearCooldownTimer();
                    setCanRetake(true);
                    return 0;
                }

                return prev - 1;
            });
        }, 1000);
    };

    const checkCooldown = async () => {
        try {
            const response = await api.quizzes.getQuizCooldown('final');
            if (response.cooldown && response.time_remaining) {
                const totalSeconds = response.time_remaining.total_seconds ||
                    (response.time_remaining.hours * 3600 + response.time_remaining.minutes * 60);

                startCooldownTimer(totalSeconds);
            }
        } catch (error) {
            console.error("Error checking quiz cooldown:", error);
        }
    };

    // On mount, pull any existing cooldown so retake timing is visible immediately
    useEffect(() => {
        checkCooldown();
    }, []);

    useEffect(() => {

        const fetchQuizData = async () => {
            try {
                setLoading(true);
                // Reset auto-submit flag when fetching new quiz data
                autoSubmitTriggered.current = false;

                // Call the API to get the quiz data
                const response = await api.quizzes.getFinalQuiz(courseId);
                setQuizData(response);
                setAvailabilityMessage('');
                setLoading(false);
            } catch (error) {
                console.error("Error fetching quiz data:", error);
                const responseData = error?.response?.data;

                if (responseData?.cooldown === true && responseData?.time_remaining) {
                    const timeRemaining = responseData.time_remaining;
                    const message = responseData.detail;

                    startCooldownTimer(timeRemaining.total_seconds);

                    setQuizData({
                        quiz: {
                            title: 'Final Assessment',
                            description: 'This comprehensive assessment tests your knowledge of all course materials.',
                            passing_score: 70,
                            estimated_time: 60
                        },
                        questions: []
                    });

                    setAvailabilityMessage(message || 'You must wait before attempting the quiz again.');

                    setSecurityAlert({
                        show: true,
                        severity: 'warning',
                        title: 'COOLDOWN PERIOD ACTIVE',
                        message: message || 'You must wait before attempting the quiz again.',
                        code: 'QUIZ_COOLDOWN_ACTIVE'
                    });
                } else {
                    const detailMessage = responseData?.detail || responseData?.message;

                    if (detailMessage) {
                        setAvailabilityMessage(detailMessage);
                        setSecurityAlert({
                            show: true,
                            severity: 'warning',
                            title: 'QUIZ UNAVAILABLE',
                            message: detailMessage,
                            code: 'QUIZ_UNAVAILABLE'
                        });
                    } else {
                        setAvailabilityMessage('');
                        setSecurityAlert({
                            show: true,
                            severity: 'warning',
                            title: 'QUIZ LOAD ERROR',
                            message: 'There was an error loading the quiz data. Please try again later.',
                            code: 'QUIZ_LOAD_FAILED'
                        });
                    }
                }

                setLoading(false);
            }
        };

        fetchQuizData();
    }, []);

    // Remove the automatic cooldown check after quiz completion
    // It's now handled directly in submitQuiz function

    useEffect(() => () => {
        clearCooldownTimer();
    }, []);

    // Preload images to prevent loading delays during quiz
    useEffect(() => {
        if (quizData) {
            quizData.questions.forEach(question => {
                if (question.image_url) {
                    const img = new Image();
                    img.src = question.image_url;
                    imageRefs.current[question.id] = img;
                }

                // If any answer options have images
                if (question.type === 'mcq') {
                    ['option_a', 'option_b', 'option_c', 'option_d'].forEach(opt => {
                        if (question[`${opt}_image`]) {
                            const img = new Image();
                            img.src = question[`${opt}_image`];
                            imageRefs.current[`${question.id}_${opt}`] = img;
                        }
                    });
                }
            });
        }
    }, [quizData]);

    const handleStartQuiz = () => {
        setQuizStarted(true);
        autoSubmitTriggered.current = false; // Reset auto-submit flag
        // Reset violations counter when starting a quiz
        resetViolations();
    };

    const handleSelectAnswer = (questionId, option) => {
        setSelectedAnswers({
            ...selectedAnswers,
            [questionId]: option
        });
    };

    const handleTextAnswer = (questionId, value) => {
        setTextAnswers({
            ...textAnswers,
            [questionId]: value
        });
    };

    const getCurrentAnswer = (question) => {
        return selectedAnswers[question.id] || '';
    };

    const isAnswerComplete = (question) => {
        return !!selectedAnswers[question.id];
    };

    const handleNextQuestion = () => {
        if (currentQuestion < quizData.questions.length - 1) {
            setCurrentQuestion(currentQuestion + 1);
        } else {
            // Last question - submit the quiz
            submitQuiz();
        }
    };

    const handlePrevQuestion = () => {
        if (currentQuestion > 0) {
            setCurrentQuestion(currentQuestion - 1);
        }
    };

    const submitQuiz = async () => {
        console.log('submitQuiz called - submitting:', submitting, 'quizCompleted:', quizCompleted);

        if (submitting) {
            console.log('Submit blocked - already submitting');
            return;
        }

        if (quizCompleted) {
            console.log('Submit blocked - quiz already completed');
            return;
        }

        console.log('Proceeding with quiz submission...');
        setSubmitting(true);

        try {
            // Prepare answer data - only MCQ answers
            const answers = { ...selectedAnswers };
            const answerCount = Object.keys(answers).length;

            // Use ref to get the most current violation count
            const currentViolations = violationsRef.current;
            console.log('Submitting quiz with violations:', currentViolations);
            console.log('Number of answers:', answerCount);

            // If no answers (auto-submit due to violations), create failed result
            if (answerCount === 0) {
                console.log('No answers provided - creating failed result due to security violations');
                const totalQuestions = quizData?.questions?.length || 0;
                const results = {
                    passed: false,
                    score: 0,
                    total_questions: totalQuestions,
                    correct_answers: 0,
                    incorrect_answers: 0,
                    unanswered: totalQuestions,
                    violations_count: currentViolations,
                    message: 'Quiz terminated due to security violations. No answers were submitted.'
                };
                setQuizResults(results);
                setQuizCompleted(true);
                setSubmitting(false);
                return;
            }

            // Submit answers to the API and get results
            const results = await api.quizzes.submitFinalQuiz(courseId, answers, currentViolations);

            console.log('Quiz submission successful:', results);

            // Set the quiz results from the API response
            setQuizResults(results);
            setQuizCompleted(true);

            // If failed, check cooldown immediately
            if (!results.passed) {
                await checkCooldown();
            }
        } catch (error) {
            console.error("Error submitting quiz:", error);
            // Handle submission error
            setSecurityAlert({
                show: true,
                severity: 'warning',
                title: 'SUBMISSION ERROR',
                message: 'There was an error submitting your quiz. Please try again.',
                code: 'QUIZ_SUBMISSION_FAILED'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleRetakeQuiz = async () => {
        try {
            // Fetch new questions for retake
            setLoading(true);
            const quizResponse = await api.quizzes.getFinalQuiz(courseId);
            setQuizData(quizResponse);
            setLoading(false);

            // Reset quiz state
            setSelectedAnswers({});
            setTextAnswers({});
            setCurrentQuestion(0);
            setQuizCompleted(false);
            setQuizResults(null);
            setQuizStarted(false);
            autoSubmitTriggered.current = false;
            resetViolations();
        } catch (error) {
            setLoading(false);
            console.error("Error preparing quiz retake:", error);
            const errorData = error?.response?.data;

            // Handle cooldown error
            if (errorData?.cooldown && errorData?.time_remaining) {
                const totalSeconds = errorData.time_remaining.total_seconds ||
                    (errorData.time_remaining.hours * 3600 + errorData.time_remaining.minutes * 60);

                startCooldownTimer(totalSeconds);

                const { hours, minutes, seconds: secs } = formatCooldownTime(totalSeconds);
                const timeText = hours > 0
                    ? `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`
                    : minutes > 0
                        ? `${minutes} minute${minutes !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`
                        : `${secs} second${secs !== 1 ? 's' : ''}`;

                setSecurityAlert({
                    show: true,
                    severity: 'warning',
                    title: 'COOLDOWN PERIOD ACTIVE',
                    message: errorData.detail || `You must wait ${timeText} before retaking this quiz.`,
                    code: 'QUIZ_COOLDOWN_ACTIVE'
                });
            } else if (error?.response?.status === 403) {
                // Handle forbidden error (no retakes allowed)
                setSecurityAlert({
                    show: true,
                    severity: 'error',
                    title: 'RETAKE NOT ALLOWED',
                    message: errorData?.detail || 'You are not allowed to retake this quiz at this time.',
                    code: 'QUIZ_RETAKE_FORBIDDEN'
                });
            } else {
                // Handle other errors
                setSecurityAlert({
                    show: true,
                    severity: 'error',
                    title: 'RETAKE ERROR',
                    message: errorData?.detail || errorData?.message || 'There was an error preparing your quiz retake. Please try again later.',
                    code: 'QUIZ_RETAKE_FAILED'
                });
            }
        }
    };

    // Format time remaining with hours, minutes, and seconds
    const formatCooldownTime = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);

        return {
            hours,
            minutes,
            seconds: secs
        };
    };

    const handleContinue = () => {
        // Navigate to the next content or back to module page
        navigate(`/learning/${courseId}/`);
    };

    // Render question based on type
    const renderQuestion = (question) => {
        return renderMCQ(question);
    };

    // Render MCQ question
    const renderMCQ = (question) => {
        return (
            <>
                <AnswersList>
                    <AnswerOption
                        selected={selectedAnswers[question.id] === 'a'}
                        onClick={() => handleSelectAnswer(question.id, 'a')}
                    >
                        <AnswerLabel>
                            <OptionLabel selected={selectedAnswers[question.id] === 'a'}>A</OptionLabel>
                            <AnswerText>{question.option_a}</AnswerText>
                        </AnswerLabel>
                        {question.option_a_image && (
                            <AnswerImage>
                                <img src={question.option_a_image} alt="Option A" />
                            </AnswerImage>
                        )}
                    </AnswerOption>

                    <AnswerOption
                        selected={selectedAnswers[question.id] === 'b'}
                        onClick={() => handleSelectAnswer(question.id, 'b')}
                    >
                        <AnswerLabel>
                            <OptionLabel selected={selectedAnswers[question.id] === 'b'}>B</OptionLabel>
                            <AnswerText>{question.option_b}</AnswerText>
                        </AnswerLabel>
                        {question.option_b_image && (
                            <AnswerImage>
                                <img src={question.option_b_image} alt="Option B" />
                            </AnswerImage>
                        )}
                    </AnswerOption>

                    <AnswerOption
                        selected={selectedAnswers[question.id] === 'c'}
                        onClick={() => handleSelectAnswer(question.id, 'c')}
                    >
                        <AnswerLabel>
                            <OptionLabel selected={selectedAnswers[question.id] === 'c'}>C</OptionLabel>
                            <AnswerText>{question.option_c}</AnswerText>
                        </AnswerLabel>
                        {question.option_c_image && (
                            <AnswerImage>
                                <img src={question.option_c_image} alt="Option C" />
                            </AnswerImage>
                        )}
                    </AnswerOption>

                    <AnswerOption
                        selected={selectedAnswers[question.id] === 'd'}
                        onClick={() => handleSelectAnswer(question.id, 'd')}
                    >
                        <AnswerLabel>
                            <OptionLabel selected={selectedAnswers[question.id] === 'd'}>D</OptionLabel>
                            <AnswerText>{question.option_d}</AnswerText>
                        </AnswerLabel>
                        {question.option_d_image && (
                            <AnswerImage>
                                <img src={question.option_d_image} alt="Option D" />
                            </AnswerImage>
                        )}
                    </AnswerOption>
                </AnswersList>
            </>
        );
    };

    // Render fill-in question
    const renderFillIn = (question) => {
        return (
            <FillInInput
                type="text"
                placeholder="Type your answer here..."
                value={textAnswers[question.id] || ''}
                onChange={(e) => handleTextAnswer(question.id, e.target.value)}
            />
        );
    };

    // Get question type icon
    const getQuestionTypeIcon = () => {
        return <FiAlertCircle />;
    };

    // Get question type label
    const getQuestionTypeLabel = () => {
        return 'Multiple Choice';
    };

    if (loading) {
        return (
            <Layout>
                <div>Loading quiz...</div>
            </Layout>
        );
    }

    if (!quizData) {
        return (
            <Layout>
                <QuizContainer>
                    <SecurityAlert
                        isOpen={securityAlert.show}
                        onClose={() => setSecurityAlert({ ...securityAlert, show: false })}
                        severity={securityAlert.severity}
                        title={securityAlert.title}
                        message={securityAlert.message}
                        code={securityAlert.code}
                        buttonText="ACKNOWLEDGE"
                    />
                    <QuizHeader>
                        <QuizTitle>Final Assessment</QuizTitle>
                        <QuizDescription>{availabilityMessage || 'Final Quiz will be available soon. Keep learning!'}</QuizDescription>
                    </QuizHeader>
                </QuizContainer>
            </Layout>
        );
    }

    const { quiz, questions } = quizData;
    const currentQ = questions[currentQuestion];
    const progressPercentage = ((currentQuestion + 1) / questions.length) * 100;

    return (
        <Layout>
            <QuizContainer>
                <SecurityAlert
                    isOpen={securityAlert.show}
                    onClose={() => setSecurityAlert({ ...securityAlert, show: false })}
                    severity={securityAlert.severity}
                    title={securityAlert.title}
                    message={securityAlert.message}
                    code={securityAlert.code}
                    buttonText="ACKNOWLEDGE"
                />
                {!quizStarted ? (
                    <QuizHeader>
                        <QuizTitle>{quiz.title || 'Final Assessment'}</QuizTitle>
                        <QuizDescription>{quiz.description || 'Test your knowledge with this comprehensive final assessment.'}</QuizDescription>


                        {/* Cooldown UI */}
                        {!canRetake && cooldownRemaining > 0 ? (
                            <CooldownContainer>
                                <CooldownIcon>
                                    <FiClock />
                                </CooldownIcon>
                                <CooldownTitle>Cooldown Period Active</CooldownTitle>
                                <CooldownText>
                                    You must wait before attempting the quiz again. This cooldown period ensures
                                    proper preparation and review of course materials.
                                </CooldownText>

                                {/* Cooldown timer display */}
                                <CooldownTimer>
                                    <TimerUnit>
                                        <TimerValue>{formatCooldownTime(cooldownRemaining).hours}</TimerValue>
                                        <TimerLabel>Hours</TimerLabel>
                                    </TimerUnit>
                                    <TimerUnit>
                                        <TimerValue>{formatCooldownTime(cooldownRemaining).minutes}</TimerValue>
                                        <TimerLabel>Minutes</TimerLabel>
                                    </TimerUnit>
                                    <TimerUnit>
                                        <TimerValue>{formatCooldownTime(cooldownRemaining).seconds}</TimerValue>
                                        <TimerLabel>Seconds</TimerLabel>
                                    </TimerUnit>
                                </CooldownTimer>
                            </CooldownContainer>
                        ) : (
                            <QuizInstructions>
                                <h3>
                                    <FiAlertCircle />
                                    Instructions
                                </h3>
                                <p>This final assessment contains multiple question types including multiple choice and questions with code examples.</p>
                                <p>You need to score at least {quiz.passing_score || 70}% to pass this assessment.</p>
                                    <p>Time limit: {quizData.quiz.estimated_time} minutes total.</p>
                                <p><strong>SECURE ENVIRONMENT NOTICE:</strong> This assessment is protected by an advanced anti-tampering system. Leaving the assessment window, using browser dev tools, or attempting to copy/paste content will trigger security protocols and may result in automatic submission.</p>
                                <p><strong>RETAKE POLICY:</strong> If you do not pass, you may retake the assessment after a 4-hour cooldown period.</p>
                            </QuizInstructions>
                        )}

                        <StartButton onClick={handleStartQuiz} disabled={!canRetake}>
                            {canRetake ? 'Start Assessment' : 'Cooldown Active'}
                        </StartButton>
                    </QuizHeader>
                ) : !quizCompleted ? (
                    <QuizContent>
                        {/* Security badge */}
                            <SecureEnvironmentBadge active={secureEnvironment}>
                            {secureEnvironment ? <FiLock /> : <FiShield />}
                            {secureEnvironment
                                ? "SECURE ENVIRONMENT"
                                : "SECURITY ALERT"}
                            </SecureEnvironmentBadge>

                        {/* Timer display */}
                        <Timer warning={timeRemaining < 60}>
                            <FiClock />
                            Time Remaining: {formatTime(timeRemaining)}
                        </Timer>

                        <QuizProgress>
                            <ProgressText>
                                Question <span>{currentQuestion + 1}</span> of {questions.length}
                            </ProgressText>
                            <ProgressBar>
                                <ProgressFill progress={progressPercentage} />
                            </ProgressBar>
                        </QuizProgress>

                        <QuestionContainer>
                            <QuestionTypeIndicator>
                                {getQuestionTypeIcon()}
                                {getQuestionTypeLabel()}
                            </QuestionTypeIndicator>

                            <QuestionText dangerouslySetInnerHTML={{ __html: currentQ.question }} />

                            {currentQ.image_url && (
                                <QuestionImage>
                                    <img src={currentQ.image_url} alt="Question Visual" />
                                </QuestionImage>
                            )}

                            {currentQ.code && (
                                <CodeContainer>
                                    <SyntaxHighlighter
                                        language={currentQ.code_language || "javascript"}
                                        style={tomorrow}
                                        showLineNumbers={true}
                                        wrapLines={true}
                                    >
                                        {currentQ.code}
                                    </SyntaxHighlighter>
                                </CodeContainer>
                            )}

                            {renderQuestion(currentQ)}
                        </QuestionContainer>

                        <NavigationButtons>
                            <NavButton
                                onClick={handlePrevQuestion}
                                disabled={currentQuestion === 0}
                                left
                            >
                                <FiArrowRight style={{ transform: 'rotate(180deg)' }} />
                                Previous
                            </NavButton>

                            <NavButton
                                onClick={handleNextQuestion}
                                disabled={!isAnswerComplete(currentQ) || submitting}
                                primary
                            >
                                {currentQuestion < questions.length - 1 ? 'Next' : submitting ? 'Submitting...' : 'Submit Quiz'}
                                {!submitting && <FiArrowRight />}
                            </NavButton>
                        </NavigationButtons>
                    </QuizContent>
                ) : (
                    <QuizContent>
                        <ResultsContainer>
                            <ResultIcon passed={quizResults.passed}>
                                {quizResults.passed ? <FiAward /> : <FiX />}
                            </ResultIcon>

                            <ResultTitle passed={quizResults.passed}>
                                {quizResults.passed ? 'Assessment Passed!' : 'Assessment Failed'}
                            </ResultTitle>

                            <ResultText>
                                {quizResults.passed
                                    ? `Congratulations! You have successfully completed the final assessment with a score of ${quizResults.score}%.`
                                    : `You did not meet the minimum passing score of ${quiz.passing_score || 70}%. Please review the material and try again after the cooldown period.`}
                                        {violationsRef.current > 0 && ` The security system detected ${violationsRef.current} security ${violationsRef.current === 1 ? 'violation' : 'violations'} during your assessment session.`}
                            </ResultText>

                            <ScoreContainer>
                                <ScoreCircle passed={quizResults.passed}>
                                    {quizResults.score}%
                                    <span>Your Score</span>
                                </ScoreCircle>
                            </ScoreContainer>

                            <ActionButtons>
                                <ActionButton
                                    onClick={handleRetakeQuiz}
                                    disabled={!canRetake || quizResults.passed}
                                >
                                    <FiRefreshCw />
                                    Retake Assessment
                                </ActionButton>

                                {quizResults.passed && (
                                    <ActionButton onClick={handleContinue} primary>
                                        <FiArrowRight />
                                        Continue
                                    </ActionButton>
                                )}
                            </ActionButtons>

                                    {!quizResults.passed && !canRetake && cooldownRemaining > 0 && (
                                <CooldownContainer>
                                    <CooldownIcon>
                                        <FiClock />
                                    </CooldownIcon>
                                            <CooldownTitle>Assessment Cooldown Active</CooldownTitle>
                                    <CooldownText>
                                                You must wait before retaking this assessment. This cooldown period ensures
                                                proper preparation and review of course materials.
                                    </CooldownText>

                                            <CooldownTimer>
                                                <TimerUnit>
                                                    <TimerValue>{formatCooldownTime(cooldownRemaining).hours}</TimerValue>
                                                    <TimerLabel>Hours</TimerLabel>
                                                </TimerUnit>
                                                <TimerUnit>
                                                    <TimerValue>{formatCooldownTime(cooldownRemaining).minutes}</TimerValue>
                                                    <TimerLabel>Minutes</TimerLabel>
                                                </TimerUnit>
                                                <TimerUnit>
                                                    <TimerValue>{formatCooldownTime(cooldownRemaining).seconds}</TimerValue>
                                                    <TimerLabel>Seconds</TimerLabel>
                                                </TimerUnit>
                                            </CooldownTimer>
                                </CooldownContainer>
                            )}
                        </ResultsContainer>
                    </QuizContent>
                )}
            </QuizContainer>
        </Layout>
    );
};

export default FinalQuizPage;